import * as React from 'react'

import {
  DataTableProjection,
  DataTableSource,
  Export as ExportFormat,
  ProjectionKind,
} from 'generated/resources/datatable.graphql'
import moment from 'moment'

import { EXPORT_FORMATS } from 'modules/core/components/DataTable/constants'
import type { FileName } from 'modules/core/components/DataTable/core/HeadBar/types'
import useProjections from 'modules/core/components/DataTable/hooks/useProjections'
import asyncAction from 'modules/core/lib/asyncAction'
import i18n from 'modules/core/lib/i18n'
import apiClient from 'modules/core/lib/mobx-rest/apiClient'

import useData from './useData'

const getInitialFileName = (fileName: string) => {
  const date = moment().format('YYYYMMDD')

  return `${date}-${fileName}`
}

function getExportProjections<Paths, SourceFields extends string>(
  projections: DataTableProjection<Paths, SourceFields>[]
) {
  return projections
    .filter((projection) => projection.kind !== ProjectionKind.Decorative)
    .map((projection) => {
      if (projection.kind === ProjectionKind.Path) {
        return {
          path: projection.path,
          title: projection.title,
        }
      }
      if (projection.kind === ProjectionKind.Custom) {
        return {
          title: projection.title,
          template: projection.template,
        }
      }
    })
}

type ValidateNoExtension<T extends string> = T extends `${string}.${string}` ? never : T

export type Export<ExportFileName extends string> = {
  fileName: ValidateNoExtension<ExportFileName>
}

export default function useExport<
  Paths,
  SourceFields extends string,
  ExportFileName extends string,
>(source: DataTableSource<Paths, SourceFields>, fileNameProp: FileName<ExportFileName>) {
  const { projections } = useProjections<Paths, SourceFields>()
  const { variables } = useData<Paths, SourceFields>()

  const [isExporting, setIsExporting] = React.useState(false)
  const [fileName, setFileName] = React.useState<string>(getInitialFileName(fileNameProp))
  const [fileFormat, setFileFormat] = React.useState<Exclude<ExportFormat, false>>(
    EXPORT_FORMATS.EXCEL
  )

  const getQuery = () => {
    const query = source.query.loc?.source?.body

    if (!query) throw new Error('Query for DataTable not found!')

    return query
  }

  const onExport = (callback?: () => void) => {
    if (isExporting) return

    const action = async () => {
      setIsExporting(true)

      try {
        // NOTE: We should not be using a mobx-rest related thingie, but right now we do not have a
        // proper way to do this...
        await apiClient().post('/data_table/exports', {
          query: getQuery(),
          variables: variables,
          projections: getExportProjections(projections),
          fileFormat,
          fileName,
        })
      } catch (error) {
        throw error
      } finally {
        setIsExporting(false)
        callback?.()
      }
    }

    asyncAction(action, {
      successMessage: i18n.t('data_table.export.success'),
    })
  }

  return {
    onExport,
    isExporting,
    fileName,
    fileFormat,
    setFileName,
    setFileFormat,
  }
}
